import React, { useMemo, useState } from 'react';

export type InlineEditorFieldType =
  | 'text'
  | 'number'
  | 'select'
  | 'checkbox'
  | 'textarea';

export interface InlineEditorOption {
  value: string;
  label: string;
}

export interface InlineEditorField {
  name: string;
  label: string;
  type: InlineEditorFieldType;
  options?: InlineEditorOption[];
  required?: boolean;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
}

export interface InlineEditorProps<T extends Record<string, unknown>> {
  title: string;
  fields: InlineEditorField[];
  initialValues?: Partial<T>;
  onSubmit: (values: T) => void | Promise<void>;
  onCancel: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  isSubmitting?: boolean;
  className?: string;
}

type FieldValue = string | number | boolean;

function defaultFor(field: InlineEditorField): FieldValue {
  if (field.type === 'checkbox') return false;
  if (field.type === 'number') return '';
  return '';
}

export default function InlineEditor<T extends Record<string, unknown>>({
  title,
  fields,
  initialValues,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isSubmitting = false,
  className,
}: InlineEditorProps<T>) {
  const initial = useMemo(() => {
    const v: Record<string, FieldValue> = {};
    fields.forEach((field) => {
      const seed = initialValues?.[field.name];
      if (seed !== undefined && seed !== null) {
        if (field.type === 'checkbox') {
          v[field.name] = Boolean(seed);
        } else {
          v[field.name] = seed as FieldValue;
        }
      } else {
        v[field.name] = defaultFor(field);
      }
    });
    return v;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [values, setValues] = useState<Record<string, FieldValue>>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const update = (name: string, next: FieldValue) => {
    setValues((prev) => ({ ...prev, [name]: next }));
    if (errors[name]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    fields.forEach((field) => {
      if (!field.required) return;
      const v = values[field.name];
      if (field.type === 'checkbox') return;
      if (v === '' || v === undefined || v === null) {
        next[field.name] = `${field.label} is required.`;
      }
    });
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    const out: Record<string, unknown> = {};
    fields.forEach((field) => {
      const raw = values[field.name];
      if (field.type === 'number' && raw !== '' && raw !== undefined) {
        const num = Number(raw);
        out[field.name] = Number.isNaN(num) ? raw : num;
      } else {
        out[field.name] = raw;
      }
    });
    await onSubmit(out as T);
  };

  const wrapperClass = ['inline-editor', className].filter(Boolean).join(' ');

  return (
    <form className={wrapperClass} onSubmit={handleSubmit} noValidate>
      <div className="inline-editor__header">
        <h3 className="inline-editor__title">{title}</h3>
      </div>
      <div className="inline-editor__fields">
        {fields.map((field) => {
          const fieldId = `inline-editor-${field.name}`;
          const error = errors[field.name];
          const value = values[field.name];
          return (
            <div
              key={field.name}
              className={
                error
                  ? 'inline-editor__field inline-editor__field--has-error'
                  : 'inline-editor__field'
              }
            >
              {field.type !== 'checkbox' && (
                <label htmlFor={fieldId} className="inline-editor__label">
                  {field.label}
                  {field.required && (
                    <span className="inline-editor__required" aria-hidden="true">
                      {' *'}
                    </span>
                  )}
                </label>
              )}
              {field.type === 'text' && (
                <input
                  id={fieldId}
                  type="text"
                  className="inline-editor__input"
                  value={String(value ?? '')}
                  placeholder={field.placeholder}
                  disabled={field.disabled || isSubmitting}
                  onChange={(e) => update(field.name, e.target.value)}
                />
              )}
              {field.type === 'number' && (
                <input
                  id={fieldId}
                  type="number"
                  inputMode="decimal"
                  className="inline-editor__input"
                  value={String(value ?? '')}
                  placeholder={field.placeholder}
                  disabled={field.disabled || isSubmitting}
                  onChange={(e) => update(field.name, e.target.value)}
                />
              )}
              {field.type === 'textarea' && (
                <textarea
                  id={fieldId}
                  className="inline-editor__textarea"
                  value={String(value ?? '')}
                  placeholder={field.placeholder}
                  disabled={field.disabled || isSubmitting}
                  onChange={(e) => update(field.name, e.target.value)}
                />
              )}
              {field.type === 'select' && (
                <select
                  id={fieldId}
                  className="inline-editor__select admin-select"
                  value={String(value ?? '')}
                  disabled={field.disabled || isSubmitting}
                  onChange={(e) => update(field.name, e.target.value)}
                >
                  {!field.required && <option value="">—</option>}
                  {field.options?.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {field.type === 'checkbox' && (
                <label htmlFor={fieldId} className="inline-editor__checkbox-label">
                  <input
                    id={fieldId}
                    type="checkbox"
                    className="inline-editor__checkbox"
                    checked={Boolean(value)}
                    disabled={field.disabled || isSubmitting}
                    onChange={(e) => update(field.name, e.target.checked)}
                  />
                  <span>{field.label}</span>
                </label>
              )}
              {field.hint && !error && (
                <span className="inline-editor__hint">{field.hint}</span>
              )}
              {error && (
                <span className="inline-editor__error" role="alert">
                  {error}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="inline-editor__actions">
        <button
          type="button"
          className="inline-editor__cancel"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {cancelLabel}
        </button>
        <button
          type="submit"
          className="inline-editor__submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
